// src/components/TeamResultsModal.jsx
import React, { useMemo } from "react";
import { gehoertZuTeam } from "../utils/teamKey";

// `onStationClick(stationNumber)`: macht die Stationszeilen anklickbar, um zur
// Rangliste der Station zu wechseln. Ohne die Funktion bleibt die Liste, wie sie
// war (so wird das Modal auch in der Teamverwaltung benutzt).
export default function TeamResultsModal({ open, onClose, team, stations = [], onStationClick }) {
  const stationResults = useMemo(() => {
    if (!open || !team) return [];

    const isKidTeam = team.event_type === "Kinder";

    return (stations || [])
      .filter((s) => {
        const allowed = s.allow_groups ?? (s.allow_kids === false ? "Erwachsene" : "Alle");
        // 🚫 Filterlogik:
        if (isKidTeam && allowed === "Erwachsene") return false;
        if (!isKidTeam && allowed === "Kinder") return false;
        return true;
      })
      .map((s) => {
        const res = Array.isArray(s.results)
          ? s.results.find((r) => gehoertZuTeam(r, team))
          : null;

        return {
          station_number: s.station_number,
          station_name: s.station_name ?? null,
          station_type: s.station_type || "normal",
          unit: s.unit || "",
          points: res?.points ?? null,
          rank: res?.rank ?? null,
          performance_score: res?.performance_score ?? null,
          estimate_value: res?.estimate_value ?? null,
          allow_groups: s.allow_groups ?? null,
        };
      })
      .sort((a, b) => (a.station_number || 0) - (b.station_number || 0));
  }, [open, team, stations]);

  if (!open || !team) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950 bg-opacity-75 p-4">
      <div className="bg-secondary-900 w-full max-w-md rounded shadow-lg overflow-hidden border border-secondary-800">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-800">
          <div>
            <h3 className="text-lg font-semibold text-white">{team.team_name}</h3>
            <p className="text-sm text-gray-400">
              Kategorie: {team.event_type}{" "}
              {team.team_type ? `• Typ: ${team.team_type}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded hover:bg-secondary-800 text-gray-400 text-sm"
          >
            Schließen
          </button>
        </div>

        {/* Body */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {stationResults.length === 0 ? (
            <p className="text-sm text-gray-500">
              Dieses Team hat noch keine passenden Stationsergebnisse.
            </p>
          ) : (
            <>
            <ul className="space-y-2">
              {stationResults.map((r) => {
                const klickbar = typeof onStationClick === "function";
                const Zeile = klickbar ? "button" : "div";
                return (
                <li key={r.station_number}>
                <Zeile
                  {...(klickbar && {
                    type: "button",
                    onClick: () => onStationClick(r.station_number),
                  })}
                  className={`w-full flex items-center justify-between gap-3 text-left bg-secondary-800 p-3 rounded ${
                    klickbar ? "hover:bg-secondary-700 transition-colors cursor-pointer" : ""
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white">
                      {r.station_name || `Station ${r.station_number}`}
                    </div>
                    <div className="text-xs text-gray-400">
                      Station #{r.station_number}
                      {r.rank != null && ` • Platz ${r.rank}`}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-primary-500">
                      {r.points != null ? `${r.points} Pkt.` : "-"}
                    </div>

                    <div className="text-xs text-gray-400">
                      {r.station_type === "estimate" ? (
                        <>
                          Schätzwert: {r.estimate_value ?? "-"} {r.unit || ""}
                        </>
                      ) : (
                        <>Ergebnis: {r.performance_score ?? "-"} {r.unit || ""}</>
                      )}
                    </div>
                  </div>
                  {klickbar && (
                    <span className="text-gray-500 flex-shrink-0" aria-hidden="true">
                      ›
                    </span>
                  )}
                </Zeile>
                </li>
                );
              })}
            </ul>
            {typeof onStationClick === "function" && (
              <p className="mt-3 text-xs text-gray-500">
                Station antippen, um die Rangliste der Station zu sehen.
              </p>
            )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-secondary-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-secondary-700 hover:bg-secondary-600 text-gray-200"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
